/**
 * RoleList - Custom RBAC role management
 *
 * Features:
 * - Table view of all roles
 * - Create custom roles with granular permissions
 * - Clone existing roles
 * - Default roles cannot be deleted
 */

import { useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import { fetchRoles, createRole, updateRole, deleteRole } from '../../../../api';
import Button from '../../../../shared/components/ui/Button';
import Modal from '../../../../shared/components/ui/Modal';
import Spinner from '../../../../shared/components/ui/Spinner';
import styles from './RoleList.module.css';

// Available permissions grouped by category (must match backend can_* keys)
const PERMISSION_GROUPS = [
  {
    name: 'Photos',
    permissions: [
      { key: 'can_view_photos', label: 'View photos' },
      { key: 'can_edit_photos', label: 'Edit photos' },
      { key: 'can_delete_photos', label: 'Delete photos' },
      { key: 'can_archive_photos', label: 'Archive photos' },
      { key: 'can_approve_photos', label: 'Approve photos' },
    ],
  },
  {
    name: 'Faces & Persons',
    permissions: [
      { key: 'can_label_faces', label: 'Label and train faces' },
      { key: 'can_manage_persons', label: 'Create and edit persons' },
      { key: 'can_delete_persons', label: 'Delete persons' },
      { key: 'can_merge_persons', label: 'Merge persons' },
    ],
  },
  {
    name: 'Teams & Matches',
    permissions: [
      { key: 'can_manage_teams', label: 'Manage teams' },
      { key: 'can_manage_matches', label: 'Manage matches' },
    ],
  },
  {
    name: 'Company',
    permissions: [
      { key: 'can_manage_company', label: 'Manage company settings and users' },
      { key: 'can_manage_profiles', label: 'Manage processing profiles' },
      { key: 'can_set_default_profile', label: 'Set default profile' },
    ],
  },
];

function RoleModal({ role, onSave, onClose, isNew = false }) {
  const [form, setForm] = useState({
    name: role?.name || '',
    description: role?.description || '',
    permissions: role?.permissions || [],
  });
  const [saving, setSaving] = useState(false);

  const togglePermission = (key) => {
    setForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter((p) => p !== key)
        : [...prev.permissions, key],
    }));
  };

  const toggleGroup = (group) => {
    const groupKeys = group.permissions.map((p) => p.key);
    const allSelected = groupKeys.every((k) => form.permissions.includes(k));

    setForm((prev) => ({
      ...prev,
      permissions: allSelected
        ? prev.permissions.filter((p) => !groupKeys.includes(p))
        : [...new Set([...prev.permissions, ...groupKeys])],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      console.error('Failed to save role:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} size="lg">
      <Modal.Header onClose={onClose}>
        {isNew ? 'Create Role' : 'Edit Role'}
      </Modal.Header>
      <Modal.Body>
        <div className={styles.formGroup}>
          <label className={styles.label}>Role Name</label>
          <input
            type="text"
            className={styles.input}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g., Senior Editor"
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Description</label>
          <input
            type="text"
            className={styles.input}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Brief description of this role"
          />
        </div>

        <div className={styles.permissionsSection}>
          <label className={styles.label}>Permissions</label>
          <div className={styles.permissionGroups}>
            {PERMISSION_GROUPS.map((group) => {
              const groupKeys = group.permissions.map((p) => p.key);
              const selectedCount = groupKeys.filter((k) =>
                form.permissions.includes(k)
              ).length;
              const allSelected = selectedCount === groupKeys.length;
              const someSelected = selectedCount > 0 && !allSelected;

              return (
                <div key={group.name} className={styles.permissionGroup}>
                  <div className={styles.groupHeader}>
                    <label className={styles.groupCheckbox}>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someSelected;
                        }}
                        onChange={() => toggleGroup(group)}
                        className={styles.checkbox}
                      />
                      <span className={styles.groupName}>{group.name}</span>
                    </label>
                    <span className={styles.groupCount}>
                      {selectedCount}/{groupKeys.length}
                    </span>
                  </div>
                  <div className={styles.permissionList}>
                    {group.permissions.map((perm) => (
                      <label key={perm.key} className={styles.permissionItem}>
                        <input
                          type="checkbox"
                          checked={form.permissions.includes(perm.key)}
                          onChange={() => togglePermission(perm.key)}
                          className={styles.checkbox}
                        />
                        <span>{perm.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || !form.name.trim()}>
          {(() => {
            if (saving) return 'Saving...';
            if (isNew) return 'Create';
            return 'Save';
          })()}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default function RoleList() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [isNewRole, setIsNewRole] = useState(false);
  // Confirmation/alert modal states (replaces browser confirm/alert)
  const [confirmDeleteRole, setConfirmDeleteRole] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);

  const loadRoles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchRoles({ limit: 100 });
      setRoles(data.results || data || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const handleCreate = () => {
    setEditingRole(null);
    setIsNewRole(true);
    setShowModal(true);
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setIsNewRole(false);
    setShowModal(true);
  };

  const handleClone = (role) => {
    setEditingRole({
      ...role,
      name: `${role.name} (Copy)`,
      is_default: false,
    });
    setIsNewRole(true);
    setShowModal(true);
  };

  const handleSave = async (formData) => {
    if (isNewRole) {
      await createRole(formData);
    } else if (editingRole) {
      await updateRole(editingRole.id, formData);
    }
    loadRoles();
  };

  const handleDelete = (role) => {
    if (role.is_default) {
      setAlertMessage('Default roles cannot be deleted.');
      return;
    }
    if (role.user_count > 0) {
      setAlertMessage(`Cannot delete role with ${role.user_count} assigned users. Reassign users first.`);
      return;
    }
    setConfirmDeleteRole(role);
  };

  const confirmDeleteRoleAction = async () => {
    if (!confirmDeleteRole) return;
    const role = confirmDeleteRole;
    setConfirmDeleteRole(null);
    try {
      await deleteRole(role.id);
      loadRoles();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  if (loading && roles.length === 0) {
    return (
      <div className={styles.loading}>
        <Spinner />
        <p>Loading roles...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Roles</h2>
          <p className={styles.subtitle}>
            Create custom roles with granular permissions
          </p>
        </div>
        <Button variant="primary" onClick={handleCreate}>
          + New Role
        </Button>
      </div>

      {error && (
        <div className={styles.error}>
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      <div className={styles.roleGrid}>
        {roles.map((role) => (
          <div
            key={role.id}
            className={clsx(styles.roleCard, role.is_default && styles.defaultRole)}
          >
            <div className={styles.roleHeader}>
              <h3 className={styles.roleName}>
                {role.name}
                {role.is_default && <span className={styles.defaultBadge}>Default</span>}
              </h3>
              <span className={styles.userCount}>
                {role.user_count || 0} users
              </span>
            </div>

            {role.description && (
              <p className={styles.roleDescription}>{role.description}</p>
            )}

            <div className={styles.permissionSummary}>
              <span className={styles.permissionCount}>
                {role.permissions?.length || 0} permissions
              </span>
            </div>

            <div className={styles.roleActions}>
              <Button variant="ghost" size="sm" onClick={() => handleEdit(role)}>
                {role.is_default ? 'View' : 'Edit'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => handleClone(role)}>
                Clone
              </Button>
              {!role.is_default && (
                <Button variant="danger" size="sm" onClick={() => handleDelete(role)}>
                  Delete
                </Button>
              )}
            </div>
          </div>
        ))}

        {roles.length === 0 && (
          <div className={styles.empty}>
            No roles found. Create one to get started.
          </div>
        )}
      </div>

      {showModal && (
        <RoleModal
          role={editingRole}
          isNew={isNewRole}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false);
            setEditingRole(null);
          }}
        />
      )}

      {/* Alert Modal (replaces browser alert) */}
      <Modal
        isOpen={!!alertMessage}
        onClose={() => setAlertMessage(null)}
        size="sm"
      >
        <Modal.Header onClose={() => setAlertMessage(null)}>
          Notice
        </Modal.Header>
        <Modal.Body>
          <p>{alertMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setAlertMessage(null)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirm Delete Role Modal */}
      <Modal
        isOpen={!!confirmDeleteRole}
        onClose={() => setConfirmDeleteRole(null)}
        size="sm"
      >
        <Modal.Header onClose={() => setConfirmDeleteRole(null)}>
          Confirm Delete
        </Modal.Header>
        <Modal.Body>
          <p>Delete role "{confirmDeleteRole?.name}"?</p>
          <p style={{ marginTop: 8, fontSize: 13, color: '#999' }}>
            This cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="ghost" onClick={() => setConfirmDeleteRole(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDeleteRoleAction}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
